import { useMemo } from "react";
import useModuleInfo from "../../../../../../hooks/useModuleInfo";
import { BillingPeriods } from "../../../../../../util/constants";

const SUBSCRIBE_TYPES = {
  UPGRADE: "upgrade",
  DOWNGRADE: "downgrade",
  CURRENT: "current",
};

function getPlanPromptType(hasCurrentPlan, isPlanGreater, isSamePlan) {
  if (!hasCurrentPlan) return SUBSCRIBE_TYPES.CURRENT;
  if (isSamePlan) return SUBSCRIBE_TYPES.CURRENT;
  if (isPlanGreater) return SUBSCRIBE_TYPES.UPGRADE;
  return SUBSCRIBE_TYPES.DOWNGRADE;
}

function updatePlanInformation(
  plans,
  currentPlan,
  selectedPeriod = BillingPeriods.MONTHLY,
) {
  if (!Array.isArray(plans) || !plans.length) {
    return [];
  }

  const currentPlanPeriod = currentPlan?.period || BillingPeriods.MONTHLY;
  const currentPlanAmount = currentPlan?.amount / 100;

  const currentPlanPrice =
    typeof currentPlanAmount === "number" ? currentPlanAmount : 0;

  return plans.map((plan) => {
    const planPrice = plan.pricesByPeriod?.[selectedPeriod]?.amount;
    const planPriceByPeriod = typeof planPrice === "number" ? planPrice : 0;

    const isSamePlan =
      (currentPlan &&
        currentPlan.name === plan.name &&
        currentPlanPeriod !== selectedPeriod) ||
      planPrice === currentPlanPrice;

    const subscribeType = getPlanPromptType(
      !!currentPlan,
      planPrice > currentPlanPrice,
      isSamePlan,
    );

    return {
      ...plan,
      amount: planPriceByPeriod,
      formattedAmount:
        plan.pricesByPeriod?.[selectedPeriod]?.formattedAmount || "",
      subscribeType,
    };
  });
}

function updatePrices(plans, period) {
  return plans.map((plan) => {
    plan.amount = plan.pricesByPeriod[period]?.amount;
    plan.formattedAmount = plan.pricesByPeriod[period]?.formattedAmount;
    return plan;
  });
}

/**
 * Custom hook for processing and managing plans data
 * @param {Array} plans - Raw plans data
 * @param {string} period - Billing period (monthly/yearly)
 * @returns {Object} Processed plans data and utilities
 */
export default function usePlansData(plans, period = BillingPeriods.MONTHLY) {
  const { plan } = useModuleInfo();

  const updatedPlansInformation = useMemo(
    () => updatePlanInformation(plans, plan, period),
    [plans, plan, period],
  );

  const updatedPlans = useMemo(
    () => updatePrices(updatedPlansInformation, period),
    [updatedPlansInformation, period],
  );

  const currentPlanPeriodSelected = useMemo(
    () => (plan?.period || BillingPeriods.MONTHLY) === period,
    [plan?.period, period],
  );

  return {
    updatedPlans,
    updatedPlansInformation,
    currentPlanPeriodSelected,
    currentPlanData: plan,
  };
}

export { SUBSCRIBE_TYPES };
